/**
 * Entry Point
 * 
 * 使用 React 18 的 createRoot API
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import 'leaflet/dist/leaflet.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { initPerformanceMonitoring } from './utils/performance';
import { initGlobalInstallPromptCapture } from './utils/pwaInstall';
import appInfo from '../package.json';

// 初始化 Sentry（錯誤監控）- 必須在應用渲染前初始化
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  import('@sentry/react').then((Sentry) => {
    const isProduction = process.env.NODE_ENV === 'production';
    const appVersion = appInfo?.version || 'unknown';

    Sentry.init({
      dsn: SENTRY_DSN,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      // 設置 release 版本號，用於追蹤錯誤對應的應用版本
      release: `hk-transport-display@${appVersion}`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
        Sentry.feedbackIntegration({
          colorScheme: 'system',
          isNameRequired: true,
          isEmailRequired: true,
          autoInject: false, // 禁用自動顯示的浮動按鈕，只通過手動觸發
          // 設置默認語言為繁體中文
          locale: 'zh-TW',
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: isProduction ? 0.1 : 1.0, // 生產環境 10%，開發環境 100% 用於測試
      // Session Replay
      replaysSessionSampleRate: isProduction ? 0.1 : 1.0, // 生產環境 10%，開發環境 100% 用於測試
      replaysOnErrorSampleRate: 1.0, // 100% 的錯誤會話會被錄製
      environment: process.env.NODE_ENV || 'development',
      // 在開發環境中，可以啟用調試模式
      debug: !isProduction,
    });
  }).catch((err) => {
    console.warn('Failed to initialize Sentry:', err);
  });
}

// 初始化 Google Analytics 4
// 參考：https://ithelp.ithome.com.tw/articles/10358894
const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
if (GA4_MEASUREMENT_ID) {
  import('react-ga4').then((module) => {
    // 動態導入時需要訪問默認導出
    const ReactGA = module.default || module;
    const isProduction = process.env.NODE_ENV === 'production';

    // 檢查 ReactGA 是否正確加載
    if (!ReactGA || typeof ReactGA.initialize !== 'function') {
      console.error('❌ react-ga4 模組加載失敗，ReactGA.initialize 不是函數');
      console.error('模組內容:', module);
      return;
    }

    // 初始化 GA4（參考文章使用簡單的 initialize，不設置 testMode）
    // 注意：testMode: true 會阻止數據發送到 GA4，只在控制台輸出
    ReactGA.initialize(GA4_MEASUREMENT_ID, {
      testMode: false, // 確保數據能發送到 GA4
      // 可選：在開發環境啟用調試模式
      ...(isProduction ? {} : {
        gtagOptions: {
          debug_mode: true,
        },
      }),
    });

    // 確認初始化成功
    if (isProduction) {
      console.log('✅ Google Analytics 4 已初始化 (生產模式)');
    } else {
      console.log('✅ Google Analytics 4 已初始化 (開發模式)');
      console.log('💡 提示：在瀏覽器控制台輸入 window.gtag 檢查是否已加載');
    }

    // 將 ReactGA 存儲到 window 對象，方便其他地方使用
    if (typeof window !== 'undefined') {
      window.ReactGA = ReactGA;
    }

    // 等待 gtag 準備好後確認
    setTimeout(() => {
      if (typeof window !== 'undefined' && window.gtag) {
        if (!isProduction) {
          console.log('✅ window.gtag 已準備好，可以開始追蹤');
        }
      } else {
        console.warn('⚠️ window.gtag 未找到，GA4 追蹤可能無法正常工作');
      }
    }, 500);
  }).catch((err) => {
    console.error('❌ 初始化 Google Analytics 失敗:', err);
  });
} else {
  if (process.env.NODE_ENV === 'development') {
    console.warn('⚠️ REACT_APP_GA4_MEASUREMENT_ID 未設置，GA4 追蹤已禁用');
  }
}

// 初始化 PostHog（可選）
const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';
if (POSTHOG_API_KEY) {
  import('posthog-js').then((posthog) => {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog initialized');
        }
      },
    });
  }).catch((err) => {
    console.warn('Failed to initialize PostHog:', err);
  });
}

// 盡早捕捉 beforeinstallprompt，避免在 React 元件掛載前遺失事件
initGlobalInstallPromptCapture();

// 啟動成功後清理恢復模式 URL 參數，並重置恢復重試計數
const RECOVERY_RETRY_KEY = 'hktrain_recovery_retry_count';
const recoveryParams = ['__recover', '__reason', '__src', '__ts'];
if (typeof window !== 'undefined') {
  try {
    window.localStorage.setItem(RECOVERY_RETRY_KEY, '0');
  } catch (e) {
    // ignore storage failures
  }

  const url = new URL(window.location.href);
  let changed = false;
  recoveryParams.forEach((param) => {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      changed = true;
    }
  });

  if (changed) {
    const cleanUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(null, '', cleanUrl);
  }
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    // 更新通知已由 App.js 中的 Dialog 處理
    // 這裡保留回調以保持向後兼容性
    console.log('Service Worker update detected');
  },
  onSuccess: (registration) => {
    console.log('Service Worker registered successfully');
  },
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// 初始化性能監控
initPerformanceMonitoring();

// 在開發環境下載入 IndexedDB 調試工具
if (process.env.NODE_ENV === 'development') {
  import('./utils/indexedDBDebug').then(() => {
    console.log('💡 IndexedDB 調試工具已載入: window.inspectIndexedDB() 和 window.clearAllIndexedDB()');
  }).catch((err) => {
    console.warn('Failed to load IndexedDB debug tools:', err);
  });
}
